// Report registry — one entry per legacy report page (modules/MPS/Resources/js/components/reports/*).
// Column sets, KPI sets, filter field sets, footer-sum rules and row colours mirror
// the legacy Vue components; query params mirror what each controller reads.

import type { ReactNode } from 'react';
import { Chip } from '@mui/material';

import { formatDate, formatDateTime, t } from '../../i18n';
import type { DrilldownKind } from './RecordDetailDialog';
import type { FilterFieldName } from './ReportFilterForm';
import { extrasSummary, isTruthyFlag, money, num, qty, relName, relNames, sumLines, valueLabel } from './reportUtils';

type Row = Record<string, unknown>;
export type RowTone = 'success' | 'warning' | 'error' | undefined;

export type ReportColumn = {
  key: string;
  /** i18n key */
  label: string;
  render?: (row: Row) => ReactNode;
  /** Numeric value (used for money/qty rendering and footer sums). */
  value?: (row: Row) => number;
  format?: 'money' | 'qty' | 'date' | 'datetime' | 'bool' | 'text';
  /** Server sort key (orderBy=<key> asc|desc). true = use `key`. */
  sortable?: boolean | string;
  align?: 'left' | 'right' | 'center';
  /** Show a footer sum for this column. */
  sum?: boolean;
  minWidth?: number;
};

export type ReportKpi = { key: string; label: string; format: 'money' | 'count' };

export type ReportConfig = {
  key: string;
  /** Legacy route paths (/reports/<alias>) resolving to this report. */
  aliases?: string[];
  title: string;
  description: string;
  summaryEndpoint?: string;
  /** Response key holding the aggregates (e.g. `sales`). */
  summaryKey?: string;
  kpis?: ReportKpi[];
  /** Tax report: response `taxes` = [sales, purchases]. */
  kpiGroups?: Array<{ title: string; index: number; kpis: ReportKpi[] }>;
  tableEndpoint?: string;
  columns?: ReportColumn[];
  defaultOrderBy?: string;
  filters?: FilterFieldName[];
  primaryRange?: 'date' | 'created_at';
  /** Items report: the created_at range maps to start_created_at / end_created_at. */
  secondaryCreatedAt?: boolean;
  statusOptions?: string[];
  typeOptions?: string[];
  /** Rows excluded from footer sums (legacy handleSummary). */
  sumExclude?: (row: Row) => boolean;
  rowTone?: (row: Row) => RowTone;
  drilldown?: DrilldownKind;
  /** Expandable row content (legacy type: 'expand'). */
  expand?: (row: Row) => ReactNode;
  /** URL query params forwarded to the table endpoint (e.g. ?user_id=). */
  urlParams?: string[];
  superOnly?: boolean;
  /** Redirect (e.g. register records live on their own CRUD page). */
  redirectTo?: string;
};

// ---------------------------------------------------------------------------
// Column helpers
// ---------------------------------------------------------------------------

const col = (key: string, label: string, extra: Partial<ReportColumn> = {}): ReportColumn => ({ key, label, ...extra });
const moneyCol = (key: string, label: string, extra: Partial<ReportColumn> = {}): ReportColumn => ({
  key,
  label,
  format: 'money',
  align: 'right',
  value: (row) => num(row[key]),
  ...extra
});
const boolCol = (key: string, label: string, extra: Partial<ReportColumn> = {}): ReportColumn => ({ key, label, format: 'bool', align: 'center', ...extra });
const dateCol = (key = 'date', label = 'report.col.date', extra: Partial<ReportColumn> = {}): ReportColumn => ({ key, label, format: 'date', ...extra });
const createdAtCol = (extra: Partial<ReportColumn> = {}): ReportColumn => ({ key: 'created_at', label: 'report.col.createdAt', format: 'datetime', sortable: true, ...extra });
const relCol = (key: string, label: string, pick: (row: Row) => unknown = (row) => row[key]): ReportColumn => ({ key, label, render: (row) => relName(pick(row)) });
const extrasCol = (): ReportColumn => ({ key: 'extra_attributes', label: 'report.col.customFields', render: (row) => extrasSummary(row.extra_attributes), minWidth: 180 });
const detailsCol = (): ReportColumn => ({ key: 'details', label: 'report.col.details', minWidth: 200 });
const kpi = (key: string, label: string, format: ReportKpi['format'] = 'money'): ReportKpi => ({ key, label, format });

const orderTone = (row: Row): RowTone => {
  if (isTruthyFlag(row.void)) return 'error';
  if (isTruthyFlag(row.draft)) return 'warning';
  if (isTruthyFlag(row.paid)) return 'success';
  return undefined;
};
const voidOrDraft = (row: Row) => isTruthyFlag(row.void) || isTruthyFlag(row.draft);

const hours = (row: Row) => {
  if (!row.out_time || !row.in_time) return null;
  const ms = Math.abs(new Date(String(row.out_time)).getTime() - new Date(String(row.in_time)).getTime());
  return Number.isFinite(ms) ? ms / 3_600_000 : null;
};

const gatewayChip = (gateway: unknown) =>
  gateway ? <Chip size="small" variant="outlined" color={gateway === 'cash' ? 'success' : gateway === 'credit_card' ? 'info' : gateway === 'cheque' ? 'secondary' : 'default'} label={valueLabel(gateway)} /> : '-';

const typeChip = (type: unknown) => (type ? <Chip size="small" color={type === 'addition' ? 'success' : 'error'} label={valueLabel(type)} /> : '-');

const orderTotalsColumns = (): ReportColumn[] => [
  moneyCol('total', 'report.col.total', { sum: true }),
  moneyCol('item_tax_amount', 'report.col.itemTax', { sum: true }),
  moneyCol('order_tax_amount', 'report.col.orderTax', { sum: true }),
  moneyCol('discount_amount', 'report.col.discount', { sum: true }),
  moneyCol('shipping', 'report.col.shipping', { sum: true })
];

const orderKpis = (entity: 'sales' | 'purchases'): ReportKpi[] => [
  kpi('grand_total', entity === 'sales' ? 'report.kpi.totalSales' : 'report.kpi.totalPurchases'),
  kpi('total_tax_amount', 'report.kpi.totalTaxes'),
  kpi('recoverable_tax_amount', 'report.kpi.recoverableTax'),
  kpi('recoverable_tax_calculated_on', 'report.kpi.recoverableTaxOn'),
  kpi('discount_amount', 'report.kpi.totalDiscount'),
  kpi('shipping', 'report.kpi.totalShipping')
];

const taxKpis = (entity: 'sales' | 'purchases'): ReportKpi[] => orderKpis(entity).slice(0, 4);

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const reportConfigs: ReportConfig[] = [
  {
    key: 'sales-report',
    aliases: ['sales'],
    title: 'report.sales.title',
    description: 'report.sales.desc',
    summaryEndpoint: 'reports/sales',
    summaryKey: 'sales',
    kpis: orderKpis('sales'),
    tableEndpoint: 'reports/sales/table',
    defaultOrderBy: 'date desc,created_at desc',
    // Legacy: date, reference, custom_fields, customer_id, user_id, draft, paid, void, pos, item_id, serial
    // (serial is a commented-out TODO in SaleReportController, so it is not offered).
    filters: ['date', 'reference', 'custom_fields', 'customer_id', 'user_id', 'item_id', 'draft', 'paid', 'void', 'pos'],
    columns: [
      dateCol('date', 'report.col.date', { sortable: true }),
      col('reference', 'report.col.reference', { sortable: true }),
      relCol('customer', 'report.col.customer'),
      boolCol('draft', 'report.col.draft', { sortable: true }),
      boolCol('paid', 'report.col.paid', { sortable: true }),
      boolCol('void', 'report.col.void', { sortable: true }),
      moneyCol('grand_total', 'report.col.grandTotal', { sum: true }),
      createdAtCol(),
      relCol('user', 'report.col.createdBy'),
      ...orderTotalsColumns(),
      extrasCol(),
      detailsCol()
    ],
    sumExclude: voidOrDraft,
    rowTone: orderTone,
    drilldown: 'sale'
  },
  {
    key: 'purchases-report',
    aliases: ['purchases'],
    title: 'report.purchases.title',
    description: 'report.purchases.desc',
    summaryEndpoint: 'reports/purchases',
    summaryKey: 'purchases',
    kpis: orderKpis('purchases'),
    tableEndpoint: 'reports/purchases/table',
    defaultOrderBy: 'date desc,created_at desc',
    // Legacy also lists reference/serial but PurchaseReportController ignores both.
    filters: ['date', 'custom_fields', 'supplier_id', 'user_id', 'item_id', 'draft', 'paid', 'void'],
    columns: [
      dateCol('date', 'report.col.date', { sortable: true }),
      col('reference', 'report.col.reference', { sortable: true }),
      relCol('supplier', 'report.col.supplier'),
      boolCol('draft', 'report.col.draft', { sortable: true }),
      boolCol('paid', 'report.col.paid', { sortable: true }),
      boolCol('void', 'report.col.void', { sortable: true }),
      moneyCol('grand_total', 'report.col.grandTotal', { sum: true }),
      createdAtCol(),
      relCol('user', 'report.col.createdBy'),
      ...orderTotalsColumns(),
      extrasCol(),
      detailsCol()
    ],
    sumExclude: voidOrDraft,
    rowTone: orderTone,
    drilldown: 'purchase'
  },
  {
    key: 'payments-report',
    aliases: ['payments'],
    title: 'report.payments.title',
    description: 'report.payments.desc',
    summaryEndpoint: 'reports/payments',
    summaryKey: 'payments',
    kpis: [
      kpi('total', 'report.kpi.totalPayments', 'count'),
      kpi('amount', 'report.kpi.totalAmount'),
      kpi('received', 'report.kpi.totalReceived', 'count'),
      kpi('received_amount', 'report.kpi.totalReceivedAmount'),
      kpi('due', 'report.kpi.totalDue', 'count'),
      kpi('due_amount', 'report.kpi.totalDueAmount'),
      kpi('customer_amount', 'report.kpi.customerAmount'),
      kpi('supplier_amount', 'report.kpi.supplierAmount')
    ],
    tableEndpoint: 'reports/payments/table',
    defaultOrderBy: 'created_at desc',
    primaryRange: 'created_at',
    // Legacy field set lists `received` but never rendered it; the controller reads it.
    filters: ['created_at', 'custom_fields', 'customer_id', 'supplier_id', 'user_id', 'received'],
    columns: [
      createdAtCol(),
      col('reference', 'report.col.reference', { sortable: true }),
      moneyCol('amount', 'report.col.amount', { sum: true }),
      col('gateway', 'report.col.gateway', { render: (row) => gatewayChip(row.gateway) }),
      relCol('account', 'report.col.account'),
      relCol('payable', 'report.col.createdFor'),
      relCol('user', 'report.col.createdBy'),
      boolCol('received', 'report.col.received', { sortable: true }),
      detailsCol()
    ],
    // Legacy sums only received payments.
    sumExclude: (row) => !isTruthyFlag(row.received),
    rowTone: (row) => (isTruthyFlag(row.received) ? undefined : 'warning'),
    drilldown: 'payment'
  },
  {
    key: 'expenses-report',
    aliases: ['expenses'],
    title: 'report.expenses.title',
    description: 'report.expenses.desc',
    summaryEndpoint: 'reports/expenses',
    summaryKey: 'expenses',
    kpis: [
      kpi('count', 'report.kpi.totalExpenses', 'count'),
      kpi('unconfirmed', 'report.kpi.totalUnconfirmed', 'count'),
      kpi('approved', 'report.kpi.totalApproved', 'count'),
      kpi('amount', 'report.kpi.totalAmount'),
      kpi('unconfirmed_amount', 'report.kpi.totalUnconfirmedAmount'),
      kpi('approved_amount', 'report.kpi.totalApprovedAmount')
    ],
    tableEndpoint: 'reports/expenses/table',
    defaultOrderBy: 'date desc,created_at desc',
    filters: ['date', 'custom_fields', 'account_id', 'category_id', 'user_id', 'title', 'details', 'approved'],
    columns: [
      dateCol('date', 'report.col.date', { sortable: true }),
      col('title', 'report.col.title', { sortable: true }),
      col('reference', 'report.col.reference', { sortable: true }),
      moneyCol('amount', 'report.col.amount', { sum: true, sortable: true }),
      col('categories', 'report.col.category', { render: (row) => relNames(row.categories) }),
      relCol('account', 'report.col.account'),
      relCol('user', 'report.col.createdBy'),
      boolCol('approved', 'report.col.approved'),
      relCol('approved_by', 'report.col.approvedBy', (row) => row.approved_by ?? row.approvedBy),
      detailsCol(),
      extrasCol(),
      createdAtCol()
    ],
    sumExclude: voidOrDraft,
    rowTone: (row) => (isTruthyFlag(row.approved) ? undefined : 'warning'),
    drilldown: 'expense'
  },
  {
    key: 'incomes-report',
    aliases: ['incomes'],
    title: 'report.incomes.title',
    description: 'report.incomes.desc',
    summaryEndpoint: 'reports/incomes',
    summaryKey: 'incomes',
    kpis: [kpi('count', 'report.kpi.totalIncomes', 'count'), kpi('amount', 'report.kpi.totalAmount')],
    tableEndpoint: 'reports/incomes/table',
    defaultOrderBy: 'date desc,created_at desc',
    filters: ['date', 'custom_fields', 'account_id', 'category_id', 'user_id', 'title', 'details'],
    columns: [
      dateCol('date', 'report.col.date', { sortable: true }),
      col('title', 'report.col.title', { sortable: true }),
      col('reference', 'report.col.reference', { sortable: true }),
      moneyCol('amount', 'report.col.amount', { sum: true, sortable: true }),
      col('categories', 'report.col.category', { render: (row) => relNames(row.categories) }),
      relCol('account', 'report.col.account'),
      relCol('user', 'report.col.createdBy'),
      detailsCol(),
      extrasCol(),
      createdAtCol()
    ],
    sumExclude: voidOrDraft,
    drilldown: 'income'
  },
  {
    key: 'tax-report',
    aliases: ['tax', 'taxes'],
    title: 'report.tax.title',
    description: 'report.tax.desc',
    summaryEndpoint: 'reports/taxes',
    summaryKey: 'taxes',
    kpiGroups: [
      { title: 'report.tax.sales', index: 0, kpis: taxKpis('sales') },
      { title: 'report.tax.purchases', index: 1, kpis: taxKpis('purchases') }
    ],
    filters: ['date']
  },
  {
    key: 'items',
    title: 'report.items.title',
    description: 'report.items.desc',
    tableEndpoint: 'reports/items',
    defaultOrderBy: 'name asc',
    filters: ['created_at', 'date', 'category_id'],
    primaryRange: 'date',
    secondaryCreatedAt: true,
    columns: [
      col('code', 'report.col.code', { sortable: true }),
      col('name', 'report.col.name', { sortable: true }),
      { key: 'sold', label: 'report.col.sold', format: 'qty', align: 'right', sum: true, value: (row) => sumLines(row.sale_items, 'quantity') },
      { key: 'sold_amount', label: 'report.col.soldAmount', format: 'money', align: 'right', sum: true, value: (row) => sumLines(row.sale_items, 'subtotal') },
      { key: 'purchased', label: 'report.col.purchased', format: 'qty', align: 'right', sum: true, value: (row) => sumLines(row.purchase_items, 'quantity') },
      { key: 'purchased_amount', label: 'report.col.purchasedAmount', format: 'money', align: 'right', sum: true, value: (row) => sumLines(row.purchase_items, 'subtotal') },
      extrasCol(),
      createdAtCol()
    ]
  },
  {
    key: 'top-items',
    aliases: ['items/top'],
    title: 'report.topItems.title',
    description: 'report.topItems.desc',
    tableEndpoint: 'reports/items/top',
    // TableTrait crashes without orderBy; legacy sorts by the `sold` subquery.
    defaultOrderBy: 'sold desc',
    filters: ['date', 'category_id', 'customer_id', 'user_id'],
    columns: [
      col('code', 'report.col.code'),
      col('name', 'report.col.name'),
      { key: 'sold', label: 'report.col.sold', format: 'qty', align: 'right', sortable: true, value: (row) => num(row.sold) }
    ]
  },
  {
    key: 'stock-adjustment-report',
    aliases: ['stock_adjustments', 'adjustments'],
    title: 'report.adjustments.title',
    description: 'report.adjustments.desc',
    summaryEndpoint: 'reports/adjustments',
    summaryKey: 'stock_adjustments',
    kpis: [
      kpi('count', 'report.kpi.totalAdjustmentsCount', 'count'),
      kpi('grand_total', 'report.kpi.totalAdjustments'),
      kpi('total_tax_amount', 'report.kpi.totalTaxes'),
      kpi('additions_total', 'report.kpi.totalAdditions'),
      kpi('damages_total', 'report.kpi.totalDamages'),
      kpi('subtractions_total', 'report.kpi.totalSubtractions')
    ],
    tableEndpoint: 'reports/adjustments/table',
    defaultOrderBy: 'date desc,created_at desc',
    // Legacy also lists reference/serial/user_id; the controller ignores them. `type` is read by the controller.
    filters: ['date', 'type', 'custom_fields', 'item_id', 'details', 'draft'],
    typeOptions: ['addition', 'subtraction', 'damage'],
    columns: [
      dateCol('date', 'report.col.date', { sortable: true }),
      col('reference', 'report.col.reference', { sortable: true }),
      col('type', 'report.col.type', { sortable: true, render: (row) => typeChip(row.type) }),
      moneyCol('grand_total', 'report.col.total', { sum: true, sortable: true }),
      relCol('user', 'report.col.createdBy'),
      boolCol('draft', 'report.col.draft', { sortable: true }),
      detailsCol(),
      createdAtCol()
    ],
    sumExclude: voidOrDraft,
    rowTone: (row) => (isTruthyFlag(row.draft) ? 'warning' : undefined),
    drilldown: 'adjustment'
  },
  {
    key: 'stock-transfer-report',
    aliases: ['stock_transfers', 'transfers'],
    title: 'report.transfers.title',
    description: 'report.transfers.desc',
    summaryEndpoint: 'reports/stock_transfers',
    summaryKey: 'stock_transfers',
    kpis: [kpi('count', 'report.kpi.totalTransfers', 'count')],
    tableEndpoint: 'reports/stock_transfers/table',
    defaultOrderBy: 'created_at desc',
    primaryRange: 'created_at',
    filters: ['created_at', 'custom_fields', 'item_id', 'user_id', 'from_location', 'to_location', 'status'],
    statusOptions: ['pending', 'transferring', 'transferred'],
    columns: [
      createdAtCol(),
      col('reference', 'report.col.reference', { sortable: true }),
      relCol('to_location', 'report.col.toLocation', (row) => row.to_location ?? row.toLocation),
      relCol('from_location', 'report.col.fromLocation', (row) => row.from_location ?? row.fromLocation),
      col('status', 'report.col.status', { render: (row) => (row.status ? valueLabel(row.status) : '-') }),
      relCol('user', 'report.col.createdBy'),
      detailsCol()
    ],
    drilldown: 'transfer'
  },
  {
    key: 'register-records',
    aliases: ['registers'],
    title: 'report.registers.title',
    description: 'report.registers.desc',
    redirectTo: '/register-records'
  },
  {
    key: 'expiring-alerts',
    aliases: ['expiring'],
    title: 'report.expiry.title',
    description: 'report.expiry.desc',
    tableEndpoint: 'alerts/expiring',
    defaultOrderBy: 'expiry_date asc',
    columns: [
      col('name', 'report.col.name', { sortable: true }),
      col('code', 'report.col.code', { sortable: true }),
      { key: 'balance', label: 'report.col.quantity', format: 'qty', align: 'center', value: (row) => num(row.balance) },
      dateCol('expiry_date', 'report.col.expiryDate', { sortable: true }),
      col('batch_no', 'report.col.batchNo')
    ],
    rowTone: (row) => (row.expiry_date && new Date(String(row.expiry_date)).getTime() < Date.now() ? 'error' : 'warning')
  },
  {
    key: 'quantity-alerts',
    aliases: ['quantity'],
    title: 'report.quantity.title',
    description: 'report.quantity.desc',
    tableEndpoint: 'alerts/quantity',
    defaultOrderBy: 'code asc',
    columns: [
      col('name', 'report.col.name', { sortable: true }),
      col('code', 'report.col.code', { sortable: true }),
      { key: 'total_quantity', label: 'report.col.totalQuantity', format: 'qty', align: 'center', value: (row) => sumLines(row.stock, 'quantity') },
      {
        key: 'location_quantity',
        label: 'report.col.locationQuantity',
        format: 'qty',
        align: 'center',
        value: (row) => num((Array.isArray(row.location_stock) ? (row.location_stock[0] as Row | undefined) : undefined)?.quantity)
      },
      {
        key: 'alert_quantity',
        label: 'report.col.alertQuantity',
        format: 'qty',
        align: 'center',
        value: (row) => num(row.alert_quantity ?? (Array.isArray(row.location_stock) ? (row.location_stock[0] as Row | undefined)?.alert_quantity : undefined))
      },
      {
        key: 'rack',
        label: 'report.col.rackLocation',
        render: (row) => {
          const ls = Array.isArray(row.location_stock) ? (row.location_stock[0] as Row | undefined) : undefined;
          return String(ls?.rack || row.rack || relName(row.location, '') || '-');
        }
      }
    ]
  },
  {
    key: 'time-clock',
    aliases: ['time_clock', 'time_clocks'],
    title: 'report.timeClock.title',
    description: 'report.timeClock.desc',
    tableEndpoint: 'reports/time_clocks',
    defaultOrderBy: 'created_at desc',
    // Legacy: /reports/time_clock?user_id= (linked from the users list).
    filters: ['user_id'],
    urlParams: ['user_id'],
    columns: [
      { key: 'in_time', label: 'report.col.in', format: 'datetime', sortable: true },
      { key: 'out_time', label: 'report.col.out', format: 'datetime', sortable: true },
      { key: 'hours', label: 'report.col.hours', align: 'right', sum: true, value: (row) => hours(row) ?? 0, render: (row) => (hours(row) == null ? '' : money(hours(row))) },
      moneyCol('rate', 'report.col.rate'),
      {
        key: 'amount',
        label: 'report.col.amount',
        align: 'right',
        sum: true,
        value: (row) => (hours(row) ?? 0) * num(row.rate),
        render: (row) => (hours(row) == null ? '' : money((hours(row) ?? 0) * num(row.rate)))
      },
      relCol('user', 'report.col.user'),
      relCol('location', 'report.col.location'),
      detailsCol(),
      createdAtCol()
    ],
    rowTone: (row) => (row.out_time ? undefined : 'success')
  },
  {
    key: 'activity-logs',
    aliases: ['logs'],
    title: 'report.logs.title',
    description: 'report.logs.desc',
    tableEndpoint: 'logs',
    defaultOrderBy: 'created_at desc',
    columns: [
      createdAtCol(),
      col('log_name', 'report.col.logName', { sortable: true }),
      col('description', 'report.col.description', { sortable: true, minWidth: 250 }),
      relCol('user', 'report.col.by', (row) => row.user ?? row.causer)
    ],
    rowTone: (row) => {
      const text = `${String(row.event ?? '')} ${String(row.description ?? '')}`;
      if (text.includes('deleted')) return 'error';
      if (text.includes('updated')) return 'warning';
      if (text.includes('created')) return 'success';
      return undefined;
    },
    expand: (row) => (
      <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>{JSON.stringify(row.properties ?? {}, null, 2)}</pre>
    ),
    drilldown: 'log'
  },
  {
    key: 'cash-register-api-logs',
    title: 'report.apiLogs.title',
    description: 'report.apiLogs.desc',
    tableEndpoint: 'cash-register-api-logs',
    defaultOrderBy: 'created_at desc',
    superOnly: true,
    columns: [
      createdAtCol(),
      col('endpoint', 'report.col.endpoint'),
      col('success', 'report.col.success', { render: (row) => (row.success ? t('status.yes') : t('status.no')) }),
      col('status_code', 'report.col.httpStatus'),
      col('resp_code', 'report.col.responseCode'),
      col('duration_ms', 'report.col.durationMs', { align: 'right' }),
      col('cashier_no', 'report.col.cashier'),
      col('register_code', 'report.col.register'),
      col('imei', 'report.col.imei')
    ],
    rowTone: (row) => (row.success ? undefined : 'error'),
    drilldown: 'apiLog'
  }
];

export function reportConfigByKey(key: string): ReportConfig | undefined {
  return reportConfigs.find((r) => r.key === key) ?? reportConfigs.find((r) => r.aliases?.includes(key));
}

/** Default cell rendering by column format. */
export function renderCell(column: ReportColumn, row: Row): ReactNode {
  if (column.render) return column.render(row);
  const raw = column.value ? column.value(row) : row[column.key];
  switch (column.format) {
    case 'money':
      return money(raw);
    case 'qty':
      return qty(raw);
    case 'date':
      return raw ? formatDate(raw) : '-';
    case 'datetime':
      return raw ? formatDateTime(raw) : '';
    case 'bool':
      return isTruthyFlag(raw) ? t('status.yes') : t('status.no');
    default:
      if (raw == null || raw === '') return '-';
      if (typeof raw === 'object') return JSON.stringify(raw);
      return String(raw);
  }
}
